import React, { useState, useEffect } from 'react';

/**
 * PaymentCountdown Component
 * Displays a live countdown timer for pending payment verification
 * Shows remaining time until payment expires (7 days from submission)
 */
const PaymentCountdown = ({ expiresAt, status }) => {
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [urgency, setUrgency] = useState('normal'); // normal, warning, critical

  useEffect(() => {
    // Only show countdown for pending payments
    if (status !== 'PENDING_VERIFICATION' && status !== 'PENDING') {
      return;
    }

    if (!expiresAt) {
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        setIsExpired(true);
        setUrgency('expired');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      // Determine urgency level
      if (days === 0 && hours < 24) {
        setUrgency('critical');
      } else if (days < 2) {
        setUrgency('warning');
      } else {
        setUrgency('normal');
      }

      if (days > 0) {
        setTimeRemaining(`${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours} hour${hours !== 1 ? 's' : ''} ${minutes} min`);
      } else {
        setTimeRemaining(`${minutes} min ${seconds} sec`);
      }

      setIsExpired(false);
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, status]);

  // Don't render if not pending
  if (status !== 'PENDING_VERIFICATION' && status !== 'PENDING') {
    return null;
  }

  if (!expiresAt) {
    return null;
  }

  const getColorClasses = () => {
    switch (urgency) {
      case 'expired':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'critical':
        return 'bg-red-50 text-red-800 border-red-300 animate-pulse';
      case 'warning':
        return 'bg-orange-50 text-orange-800 border-orange-300';
      default:
        return 'bg-blue-50 text-blue-800 border-blue-300';
    }
  };

  const getIcon = () => {
    if (isExpired) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 text-sm font-semibold ${getColorClasses()}`}>
      {getIcon()}
      <span>
        {isExpired ? 'Expired' : `Expires in ${timeRemaining}`}
      </span>
    </div>
  );
};

export default PaymentCountdown;
